import React from 'react';
import styled from 'styled-components';
import Colours from '@shared/ui/atoms/Colours';
import Icon, { IconTypes } from '@shared/ui/atoms/icon/Icon';
import { SignageFloor } from '../data/signage-service';
import { SignageViewMode } from '../storage/signageStorage';

const SLIDE_DURATION_MS = 250;

interface Props {
  open: boolean;
  view: SignageViewMode;
  onSelectView: (view: SignageViewMode) => void;
  floors: SignageFloor[];
  selectedFloorId: number | null;
  onSelectFloor: (floorId: number) => void;
  onClose: () => void;
}

/**
 * Screen menu that slides in from the left edge over the whole page: which
 * whereabouts board to show, and which floor it shows.
 */
export function SignageMenuDrawer({ open, view, onSelectView, floors, selectedFloorId, onSelectFloor, onClose }: Props) {
  // The away boards cover the whole office, so no floor is "current" on them.
  const floorView = view === 'map' || view === 'office';
  return (
    <>
      {open && <Backdrop onClick={onClose} />}
      <Drawer $open={open} aria-label="Screen menu" aria-hidden={!open}>
        <CloseButton onClick={onClose} aria-label="Close menu">
          <Icon icon={IconTypes.Close} style={{ fontSize: 14 }} />
        </CloseButton>

        <SectionTitle>Whereabouts</SectionTitle>
        <List>
          <Row onClick={() => onSelectView('map')}>
            <RowIcon><MapIcon /></RowIcon>
            <RowLabel>Map view</RowLabel>
            {view === 'map' && <SelectedTick />}
          </Row>
          <Row onClick={() => onSelectView('office')}>
            <RowIcon><Icon icon={IconTypes.Office} color="darkGrey" style={{ fontSize: 16 }} /></RowIcon>
            <RowLabel>Office view</RowLabel>
            {view === 'office' && <SelectedTick />}
          </Row>
          <Row onClick={() => onSelectView('outOfOffice')}>
            <RowIcon><DoorIcon /></RowIcon>
            <RowLabel>Out of Office</RowLabel>
            {view === 'outOfOffice' && <SelectedTick />}
          </Row>
          <Row onClick={() => onSelectView('workingElsewhere')}>
            <RowIcon><PinIcon /></RowIcon>
            <RowLabel>Working elsewhere</RowLabel>
            {view === 'workingElsewhere' && <SelectedTick />}
          </Row>
        </List>

        <SectionTitle>Office wayfinder</SectionTitle>
        <List>
          {floors.map(floor => (
            <Row key={floor.id} onClick={() => onSelectFloor(floor.id)}>
              <RowLabel>{floor.label}</RowLabel>
              {floorView && floor.id === selectedFloorId && <SelectedTick />}
            </Row>
          ))}
        </List>
      </Drawer>
    </>
  );
}

// Drawn rather than the font's Tick glyph, which is too thin to read at this
// size.
function SelectedTick() {
  return (
    <TickCircle>
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 7.4l2.6 2.6L11 4.4" />
      </svg>
    </TickCircle>
  );
}

// The icon font has no map, door or map pin, so these are drawn to match its
// line weight.
function MapIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round">
      <path d="M1.5 3.5L5.5 2l5 1.5 4-1.5v10.5l-4 1.5-5-1.5-4 1.5z" />
      <path d="M5.5 2v10.5M10.5 3.5V14" />
    </svg>
  );
}

function DoorIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round">
      <path d="M8 2.5h5v11H8" />
      <path d="M3 2.5L8 1v14l-5-1.5z" />
    </svg>
  );
}

function PinIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round">
      <path d="M8 14.5s4.5-4.2 4.5-8a4.5 4.5 0 0 0-9 0c0 3.8 4.5 8 4.5 8z" />
      <circle cx="8" cy="6.5" r="1.6" />
    </svg>
  );
}

// Swallows the tap that closes the menu so it doesn't also land on the map.
const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 99;
`;

const Drawer = styled.nav<{ $open: boolean }>`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  width: 300px;
  max-width: 85vw;
  z-index: 100;
  background: white;
  box-shadow: 8px 0 24px rgba(46, 74, 148, 0.12);
  padding: 72px 28px 28px;
  overflow-y: auto;
  transform: translateX(${p => p.$open ? '0' : '-100%'});
  visibility: ${p => p.$open ? 'visible' : 'hidden'};
  transition: transform ${SLIDE_DURATION_MS}ms ease,
    visibility 0s linear ${p => p.$open ? '0ms' : `${SLIDE_DURATION_MS}ms`};
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 16px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: ${Colours.blue20};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const SectionTitle = styled.div`
  font-family: "soleil", sans-serif;
  font-weight: 700;
  font-size: 20px;
  color: ${Colours.black};
  margin-bottom: 6px;

  & ~ & {
    margin-top: 40px;
  }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
`;

const Row = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 44px;
  padding: 0;
  border: none;
  border-bottom: 1px solid ${Colours.lightGrey};
  background: none;
  text-align: left;
  color: ${Colours.black};
  cursor: pointer;
`;

const RowIcon = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  color: ${Colours.darkGrey};
`;

const RowLabel = styled.span`
  flex: 1;
  min-width: 0;
  font-family: "proxima-nova", sans-serif;
  font-weight: 600;
  font-size: 17px;
  color: ${Colours.black};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TickCircle = styled.span`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${Colours.blue};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;
